import { clientConfig } from '@/config/clientConfig';
import { JobDetailsModal } from '@/domains/calendar/components/JobDetailsModal';
import { computeDurationStatus, computeSpannedDays } from '@/domains/calendar/lib/durationStatus';
import { isWithinRecentWindow } from '@/domains/calendar/lib/recentJobs';
import { sortJobsByTitle, sortTeamsByName } from '@/domains/calendar/lib/scheduleOrdering';
import {
  formatZonedDate,
  formatZonedTime,
  zonedWallTimeToInstant,
} from '@/domains/calendar/lib/schedulingTime';
import type { Job } from '@/domains/jobs/components/JobCard';
import { useDurationChallengeWorkOrder } from '@/domains/jobs/hooks/useWorkOrderActions';
import { CheckboxMultiSelect } from '@/shared/components/CheckboxMultiSelect';
import { MultiSelectAutocomplete } from '@/shared/components/MultiSelectAutocomplete';
import { PageHeader } from '@/shared/components/PageHeader';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import type { ColorTheme } from '@/shared/lib/colorThemes';
import { formatMinutesCompact } from '@/shared/lib/duration-utils';
import { showErrorToast } from '@/shared/notifications/toast-utils';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { addDays, format } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Download,
  GanttChart,
  Globe,
  LayoutGrid,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';
import { useTeamCapacities } from '../hooks/useTeamCapacities';
import { exportGanttToPDF, exportGanttToPNG } from '../utils/exportGantt';
import { AIAutoScheduleModal } from './AIAutoScheduleModal';
import { CalendarGridTeamRows } from './CalendarGridTeamRows';
import { DraggableJobCard } from './DraggableJobCard';
import { PlannerMapView } from './PlannerMapView';
import { ScheduleUtilizationLegend } from './ScheduleUtilizationLegend';
import { ScheduleUtilizationSummary } from './ScheduleUtilizationSummary';
import { SchedulerPaginationNotice } from './SchedulerPaginationNotice';
import { GanttView } from './gantt';
import {
  CalendarToolbar,
  type GanttRange,
  GanttViewControls,
  type GanttZoomLevel,
  GridViewControls,
  MAP_DAYS_DEFAULT,
  MapViewControls,
  ganttDaysToShow,
  ganttRangeStart,
  ganttStepDate,
  gridRangeStart,
  gridStepDate,
  mapRangeStart,
  mapStepDate,
} from './toolbar';

export interface CalendarTeam {
  id: string;
  name: string;
  memberCount?: number;
  // Daily Job Capacity (team.max_capacity). null/undefined = unlimited (SC-6668).
  maxCapacity?: number | null;
}

export interface CalendarFilterValues {
  category: string;
}

interface FilterOption {
  id: number;
  name: string;
}

interface CalendarViewProps {
  jobs: Job[];
  onUpdateJob: (jobId: string, updatedJob: Partial<Job>) => void | Promise<void>;
  onJobClick?: (jobId: string) => void;
  colorTheme?: ColorTheme;
  onNavigateToFieldWorker?: (jobId: string) => void;
  teams: CalendarTeam[];
  search?: string;
  onSearchChange?: (search: string) => void;
  filters: CalendarFilterValues;
  onFiltersApply: (filters: CalendarFilterValues) => void;
  selectedTeams?: string[];
  onSelectedTeamsChange?: (teamIds: string[]) => void;
  selectedPriorities?: string[];
  onSelectedPrioritiesChange?: (priorityIds: string[]) => void;
  /**
   * Selected area ids (SC-6251). Applied server-side by the parent — the backend
   * derives a job's area from its postcode — so this component only renders the
   * control and does not filter `jobs` by it.
   */
  selectedAreas?: string[];
  onSelectedAreasChange?: (areaIds: string[]) => void;
  /**
   * Unified day-count shared by the Calendar-grid and Map views (SC-6371).
   * Controlled/uncontrolled like `search`/`selectedTeams`: when the parent
   * lifts it (SchedulePage → URL), a selection made in one view is retained on
   * switching to the other; when omitted (isolated tests), internal state is
   * used. Gantt ignores this — its span comes from its zoom level.
   */
  dayCount?: number;
  onDayCountChange?: (days: number) => void;
  priorities?: FilterOption[];
  categories?: FilterOption[];
  areas?: FilterOption[];
  /** Scheduled-jobs auto-pagination status (WRK-5235) — omit when paging isn't wired up. */
  scheduledPagination?: {
    /** Number of scheduled jobs loaded so far across all fetched pages. */
    loaded: number;
    /** Total scheduled jobs available on the server. */
    total: number;
    /** Whether further pages are still being auto-loaded in the background. */
    isLoadingAll: boolean;
  };
}

interface ScheduleSearchInputProps {
  initialValue: string;
  onDebouncedChange: (value: string) => void;
}

/**
 * Owns the raw keystroke state locally so typing only re-renders this small
 * input, not the entire (expensive) calendar tree above it. The parent is
 * notified once typing settles, via `onDebouncedChange` — this is what makes
 * the search box feel snappy even with a large jobs list.
 */
export function ScheduleSearchInput({ initialValue, onDebouncedChange }: ScheduleSearchInputProps) {
  const [value, setValue] = useState(initialValue);
  const debouncedValue = useDebouncedValue(value, 250);
  const lastNotified = useRef(initialValue);

  useEffect(() => {
    if (debouncedValue !== lastNotified.current) {
      lastNotified.current = debouncedValue;
      onDebouncedChange(debouncedValue);
    }
  }, [debouncedValue, onDebouncedChange]);

  // Adopt an externally-changed value ONLY when it isn't just our own debounced
  // value echoing back through the parent (and the URL). Without this guard,
  // fast typing gets clobbered: the parent re-renders when the API results
  // arrive and pushes a slightly-stale value back down, snapping the input
  // back to what it was a few keystrokes ago.
  useEffect(() => {
    if (initialValue !== lastNotified.current) {
      lastNotified.current = initialValue;
      setValue(initialValue);
    }
  }, [initialValue]);

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input
        placeholder="Search by name, location, ID..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="pl-9 w-[260px] bg-background border-border text-foreground h-10 text-sm"
      />
    </div>
  );
}

function UnscheduledDropZone({
  children,
  onDrop,
}: { children: React.ReactNode; onDrop: (job: Job) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'JOB',
    // SC-6670: a read-only schedule refuses every drop, so un-scheduling by
    // dragging a job back to the backlog is off too.
    canDrop: (item: Job) => clientConfig.features.scheduleDragAndDrop && !!item.scheduledDate,
    drop: (item: Job) => {
      if (item.scheduledDate) onDrop(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  drop(ref);
  return (
    <div
      ref={ref}
      className={`flex-1 overflow-y-auto p-3 transition-colors ${isOver && canDrop ? 'bg-amber-50 dark:bg-amber-950/20 ring-2 ring-inset ring-amber-400 rounded' : ''}`}
    >
      {isOver && canDrop && (
        <div className="flex items-center justify-center gap-2 mb-3 py-2 px-3 rounded-md bg-amber-100 dark:bg-amber-900/40 border border-amber-300 dark:border-amber-700">
          <span className="text-xs font-medium text-amber-800 dark:text-amber-200">
            Drop here to unschedule
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

interface CalendarCellProps {
  teamId: string;
  date: string;
  jobs: Job[];
  jobSpans: Map<string, number>;
  onDrop: (job: Job, teamId: string, date: string) => void;
  variant: 'minimal' | 'vibrant' | 'pastel' | 'gradient' | 'professional';
  onJobClick?: (jobId: string) => void;
  // SC-6668: the team has reached its Daily Job Capacity for this day, so no
  // further job may be booked into this team/day cell.
  atCapacity?: boolean;
}

function CalendarCell({
  teamId,
  date,
  jobs,
  jobSpans,
  onDrop,
  variant,
  onJobClick,
  atCapacity = false,
}: CalendarCellProps) {
  const today = format(new Date(), 'yyyy-MM-dd');
  const isPast = date < today;

  const dropRef = useRef<HTMLDivElement>(null);
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: 'JOB',
    // SC-6668: reject the drop when the team is at capacity for this day, in
    // addition to the existing past-date restriction. SC-6670: read-only
    // brands never accept a drop.
    canDrop: () => clientConfig.features.scheduleDragAndDrop && !isPast && !atCapacity,
    drop: (item: Job) => onDrop(item, teamId, date),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  drop(dropRef);
  const isToday = today === date;
  // Reuse the existing rejected-cell affordance; the message reflects which
  // rule blocked the drop (past date takes precedence over capacity).
  const rejectMessage = !clientConfig.features.scheduleDragAndDrop
    ? 'Schedule is read-only'
    : isPast
      ? 'Cannot schedule in the past'
      : 'Team at daily job capacity';

  return (
    <div
      ref={dropRef}
      className={`relative min-h-[100px] p-2 border-r border-b border-border transition-colors ${
        isOver && canDrop ? 'bg-blue-50 dark:bg-blue-950/20' : ''
      } ${isOver && !canDrop ? 'bg-red-50 dark:bg-red-950/20' : ''} ${isPast ? 'bg-muted/30 opacity-60' : ''} ${isToday && !isOver ? 'bg-blue-50/30 dark:bg-blue-950/10' : ''} ${!isPast && !isToday && !isOver ? 'bg-background' : ''}`}
    >
      {isOver && !canDrop && (
        <div className="absolute inset-x-0 top-1 flex justify-center z-20 pointer-events-none">
          <div className="bg-red-600 text-white text-xs font-medium px-3 py-1.5 rounded-md shadow-lg">
            {rejectMessage}
          </div>
        </div>
      )}
      <div className="space-y-2">
        {jobs.map((job, index) => {
          const span = jobSpans.get(job.id) ?? 1;
          return (
            <div
              key={job.id}
              className="relative"
              style={
                span > 1
                  ? {
                      width: `calc(100% * ${span} + ${(span - 1) * 1}px)`,
                      maxWidth: `calc(100% * ${span} + ${(span - 1) * 1}px)`,
                      zIndex: 10,
                    }
                  : undefined
              }
            >
              <DraggableJobCard
                job={job}
                isScheduled
                variant={variant}
                index={index}
                onJobClick={onJobClick}
              />
              {span > 1 && (
                <div className="absolute bottom-1.5 right-2 z-20">
                  <span className="text-[10px] font-medium bg-blue-600 text-white px-1.5 py-0.5 rounded">
                    {span} days
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * SC-6668: count DISTINCT booked jobs per `teamId|scheduledDate`.
 *
 * Derived from the already-loaded scheduled jobs — deliberately NOT filtered by
 * search or the team selector, because a team's capacity is a function of what
 * is actually booked against it, not of what the current view happens to show.
 * Each scheduled job is one actual booking (the backend `getScheduledJobs`
 * collapses to one row per live `schedule.job_id`), so a multi-member team's
 * single job counts once, not once per member. A job id is de-duplicated per
 * cell as a defensive guard.
 */
export function buildBookedJobCounts(jobs: Job[]): Map<string, number> {
  const counts = new Map<string, number>();
  const seen = new Set<string>();
  for (const job of jobs) {
    if (!job.teamId || !job.scheduledDate) continue;
    const dedupeKey = `${job.teamId}|${job.scheduledDate}|${job.id}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    const key = `${job.teamId}|${job.scheduledDate}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/**
 * SC-6668: a team/day is at capacity once its booked job count reaches the
 * team's Daily Job Capacity. A null/0/undefined capacity means unlimited,
 * preserving the prior behaviour for teams without a configured capacity.
 */
export function isTeamDayAtCapacity(
  bookedCount: number,
  maxCapacity: number | null | undefined,
): boolean {
  return maxCapacity != null && maxCapacity > 0 && bookedCount >= maxCapacity;
}

export function CalendarView({
  jobs,
  onUpdateJob,
  onJobClick,
  colorTheme: _colorTheme,
  onNavigateToFieldWorker,
  teams: teamsProp,
  search,
  onSearchChange,
  filters,
  onFiltersApply,
  selectedTeams: selectedTeamsProp,
  onSelectedTeamsChange,
  selectedPriorities: selectedPrioritiesProp,
  onSelectedPrioritiesChange,
  selectedAreas: selectedAreasProp,
  onSelectedAreasChange,
  dayCount: dayCountProp,
  onDayCountChange,
  priorities = [],
  categories = [],
  areas = [],
  scheduledPagination,
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  // `searchTerm` only updates once the user pauses typing — the keystroke
  // state itself lives in <ScheduleSearchInput>, isolated from this (large)
  // component so typing doesn't re-render the whole calendar tree per key.
  const [searchTerm, setSearchTerm] = useState(search ?? '');
  // Single source of truth for the day-count, shared by the grid and map views
  // (SC-6371). Falls back to internal state when the parent doesn't lift it
  // (isolated tests) — mirrors the controlled/uncontrolled pattern used for
  // search / selectedTeams above.
  const [internalDayCount, setInternalDayCount] = useState(3);
  const dayView = dayCountProp ?? internalDayCount;
  const setDayView = onDayCountChange ?? setInternalDayCount;
  // The Map view only follows the shared count for brands that expose its range
  // selector (mapViewRangeSelector). Where the selector is hidden
  // (works-lite/Cadent) the map keeps its fixed default window, unchanged.
  const mapDays = clientConfig.features.mapViewRangeSelector ? dayView : MAP_DAYS_DEFAULT;
  // Falls back to internal state when the team filter isn't lifted to a
  // parent (e.g. in isolated tests) — mirrors the controlled/uncontrolled
  // pattern used for search above.
  const [internalSelectedTeams, setInternalSelectedTeams] = useState<string[]>([]);
  const selectedTeams = selectedTeamsProp ?? internalSelectedTeams;
  const setSelectedTeams = onSelectedTeamsChange ?? setInternalSelectedTeams;
  const [internalSelectedPriorities, setInternalSelectedPriorities] = useState<string[]>([]);
  const selectedPriorities = selectedPrioritiesProp ?? internalSelectedPriorities;
  const setSelectedPriorities = onSelectedPrioritiesChange ?? setInternalSelectedPriorities;
  const [internalSelectedAreas, setInternalSelectedAreas] = useState<string[]>([]);
  const selectedAreas = selectedAreasProp ?? internalSelectedAreas;
  const setSelectedAreas = onSelectedAreasChange ?? setInternalSelectedAreas;

  // Teams arrive from the API in an effectively random order (WRK-5718).
  // Sort once here, A-Z, so every consumer — grid rows, Gantt rows, the team
  // filter, and the map view — shares the same predictable order.
  const teams = useMemo(() => sortTeamsByName(teamsProp), [teamsProp]);

  const handleSearchDebouncedChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      onSearchChange?.(value);
    },
    [onSearchChange],
  );

  const setCategoryFilter = (value: string) => {
    onFiltersApply({ ...filters, category: value });
  };
  const [showAIScheduleModal, setShowAIScheduleModal] = useState(false);
  const [showJobDetailsModal, setShowJobDetailsModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'gantt' | 'map'>('grid');

  // Gantt-specific state. The zoom level sets the visible span, and the range
  // selector overrides it (SC-6252). Null means "follow the zoom", so changing
  // zoom always lands on that zoom's natural window until the planner picks a
  // range of their own.
  const [ganttZoomLevel, setGanttZoomLevel] = useState<GanttZoomLevel>('week');
  const [ganttRangeOverride, setGanttRangeOverride] = useState<GanttRange | null>(null);

  const changeGanttZoom = (zoom: GanttZoomLevel) => {
    setGanttZoomLevel(zoom);
    setGanttRangeOverride(null);
  };
  const [exportingFormat, setExportingFormat] = useState<'png' | 'pdf' | null>(null);
  const isExporting = exportingFormat !== null;
  const [unscheduleConfirm, setUnscheduleConfirm] = useState<Job | null>(null);
  const [schedulePrompt, setSchedulePrompt] = useState<{
    job: Job;
    teamId: string;
    date: string;
  } | null>(null);
  const [scheduleStartTime, setScheduleStartTime] = useState('08:00');
  const [scheduleEndDate, setScheduleEndDate] = useState('');
  const [scheduleEndTime, setScheduleEndTime] = useState('');
  const [isSubmittingSchedule, setIsSubmittingSchedule] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(340);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [ganttViewportWidth, setGanttViewportWidth] = useState(0);
  const isResizing = useRef(false);
  const ganttContainerRef = useRef<HTMLDivElement>(null);

  const durationChallengeMutation = useDurationChallengeWorkOrder();

  const designVariant = 'professional';

  // Interpret the times typed in the Schedule modal in the brand's business
  // timezone, not the planner's browser zone (WRK-5697).
  const schedulingTimeZone = clientConfig.features.schedulingTimeZone;

  // Per-view date window. Each helper snaps to its natural start (Mon for
  // week, 1st for month, today otherwise) so the visible range matches what
  // the active zoom button says.
  const ganttRange = useMemo(() => {
    const start = ganttRangeStart(ganttZoomLevel, currentDate);
    return { start, days: ganttDaysToShow(ganttZoomLevel, currentDate, ganttRangeOverride) };
  }, [ganttZoomLevel, currentDate, ganttRangeOverride]);

  const viewStart = useMemo(() => {
    if (viewMode === 'gantt') return ganttRange.start;
    if (viewMode === 'map') return mapRangeStart(currentDate);
    return gridRangeStart(currentDate);
  }, [viewMode, currentDate, ganttRange.start]);

  const visibleDaysToShow =
    viewMode === 'gantt' ? ganttRange.days : viewMode === 'map' ? mapDays : dayView;

  const weekDays = Array.from({ length: dayView }, (_, i) => addDays(viewStart, i));

  // Capacity / utilization window: each view uses its own visible span.
  const capacityRangeEndExclusive = addDays(viewStart, visibleDaysToShow);
  const teamIds = teams.map((t) => t.id);
  const {
    capacitiesByTeam,
    summariesByTeam,
    membersByTeam,
    overall: capacityOverall,
    isLoading: capacityLoading,
  } = useTeamCapacities(teamIds, viewStart, capacityRangeEndExclusive);

  const handleDrop = (job: Job, teamId: string, date: string) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    if (date < today) return;
    setScheduleStartTime('08:00');
    const durationMinutes = job.duration ?? (job.estimatedHours ?? 1) * 60;
    const defaultEnd = new Date(
      zonedWallTimeToInstant(date, '08:00', schedulingTimeZone).getTime() +
        durationMinutes * 60 * 1000,
    );
    setScheduleEndDate(formatZonedDate(defaultEnd, schedulingTimeZone));
    setScheduleEndTime(formatZonedTime(defaultEnd, schedulingTimeZone));
    setSchedulePrompt({ job, teamId, date });
  };

  const confirmSchedule = async () => {
    if (!schedulePrompt) return;
    if (isSubmittingSchedule) return;
    const { job: droppedJob, teamId, date } = schedulePrompt;

    const startDt = zonedWallTimeToInstant(date, scheduleStartTime, schedulingTimeZone);
    // Guard against scheduling in the past even if the button state is bypassed
    // (WRK-5697). The banner/disabled button are the primary UX, this is defence.
    // startDt is anchored to the business timezone so this compares like-for-like
    // against the real current instant regardless of the scheduler's browser zone.
    if (startDt.getTime() < Date.now()) {
      showErrorToast('Unable to schedule job', 'Start time is in the past');
      return;
    }

    const startISO = startDt.toISOString();
    const endISO = zonedWallTimeToInstant(
      scheduleEndDate,
      scheduleEndTime,
      schedulingTimeZone,
    ).toISOString();

    setIsSubmittingSchedule(true);
    try {
      await onUpdateJob(droppedJob.id, {
        scheduledDate: date,
        teamId,
        startTime: startISO,
        endTime: endISO,
      });
      setSchedulePrompt(null);
    } catch (err) {
      // Coverage / conflict rejections come back as a 400 with a friendly
      // message (e.g. the job's area is outside the team's coverage, WRK-5550).
      // Surface it and keep the board unchanged so the user can pick another team.
      showErrorToast(
        'Unable to schedule job',
        err instanceof Error ? err.message : 'Failed to schedule job',
      );
      setSchedulePrompt(null);
    } finally {
      setIsSubmittingSchedule(false);
    }
  };

  const handleUnschedule = (job: Job) => {
    setUnscheduleConfirm(job);
  };

  const confirmUnschedule = () => {
    if (!unscheduleConfirm) return;
    onUpdateJob(unscheduleConfirm.id, {
      scheduledDate: undefined,
      teamId: undefined,
    });
    setUnscheduleConfirm(null);
  };

  const handleJobClick = (jobId: string) => {
    setSelectedJobId(jobId);
    setShowJobDetailsModal(true);
  };

  const handleExportPNG = async () => {
    setExportingFormat('png');
    try {
      await exportGanttToPNG(
        'gantt-chart-container',
        `gantt-${format(new Date(), 'yyyy-MM-dd')}.png`,
      );
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExportingFormat(null);
    }
  };

  const handleExportPDF = async () => {
    setExportingFormat('pdf');
    try {
      await exportGanttToPDF(
        'gantt-chart-container',
        `gantt-${format(new Date(), 'yyyy-MM-dd')}.pdf`,
      );
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setExportingFormat(null);
    }
  };

  // Track Gantt viewport width for responsive column sizing
  useEffect(() => {
    const updateGanttWidth = () => {
      if (ganttContainerRef.current) {
        const width = ganttContainerRef.current.offsetWidth;
        setGanttViewportWidth(width);
      }
    };

    // Initial measurement
    updateGanttWidth();

    // Re-measure on window resize
    window.addEventListener('resize', updateGanttWidth);

    // Re-measure when sidebar width changes
    const resizeObserver = new ResizeObserver(updateGanttWidth);
    if (ganttContainerRef.current) {
      resizeObserver.observe(ganttContainerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateGanttWidth);
      resizeObserver.disconnect();
    };
  }, [sidebarWidth, sidebarCollapsed]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = Math.max(200, Math.min(600, startWidth + (moveEvent.clientX - startX)));
      setSidebarWidth(newWidth);
    };

    const onMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const activeFiltersCount = Object.values(filters).filter(
    (value) => value !== '' && value !== 'all',
  ).length;

  // Whether anything is narrowing the job list. Drives the unscheduled panel's
  // empty state, so an empty list caused by a filter never reads as
  // "All jobs scheduled!" (SC-6251 — the Area filter made this misleading).
  const hasActiveFilters =
    Boolean(searchTerm) ||
    activeFiltersCount > 0 ||
    selectedTeams.length > 0 ||
    selectedPriorities.length > 0 ||
    selectedAreas.length > 0;

  const matchesSearchTerm = useCallback(
    (job: Job) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        job.title.toLowerCase().includes(term) ||
        job.description?.toLowerCase().includes(term) ||
        job.location?.toLowerCase().includes(term) ||
        job.id.toLowerCase().includes(term) ||
        job.client?.toLowerCase().includes(term) ||
        job.assignee?.toLowerCase().includes(term)
      );
    },
    [searchTerm],
  );

  // Jobs matching search + team filters, indexed by "teamId|date" so each grid
  // cell is an O(1) map lookup instead of a full re-scan of every job — this
  // index is rebuilt once per render instead of once per cell (teams × days).
  const jobsByTeamAndDate = useMemo(() => {
    const index = new Map<string, Job[]>();
    for (const job of jobs) {
      if (!job.teamId || !job.scheduledDate) continue;
      if (selectedTeams.length > 0 && !selectedTeams.includes(job.teamId)) continue;
      if (!matchesSearchTerm(job)) continue;

      const key = `${job.teamId}|${job.scheduledDate}`;
      const existing = index.get(key);
      if (existing) {
        existing.push(job);
      } else {
        index.set(key, [job]);
      }
    }
    return index;
  }, [jobs, selectedTeams, matchesSearchTerm]);

  const getJobsForCell = (teamId: string, date: string) => {
    return jobsByTeamAndDate.get(`${teamId}|${date}`) ?? [];
  };

  // SC-6668: distinct booked jobs per team/day, used to enforce Daily Job
  // Capacity at the booking gate. Computed from all scheduled jobs (not the
  // search/team-filtered view) so hiding a job from the board can't free up
  // capacity that is still occupied.
  const bookedJobCountsByTeamDate = useMemo(() => buildBookedJobCounts(jobs), [jobs]);

  const getJobSpanDays = (job: Job): number => {
    if (!job.scheduledDate) return 1;
    const endDate = job.scheduledEndDate ?? job.scheduledDate;
    if (endDate === job.scheduledDate) return 1;
    const startMs = new Date(job.scheduledDate).getTime();
    const endMs = new Date(endDate).getTime();
    const days = Math.ceil((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
    const startIdx = weekDays.findIndex((d) => format(d, 'yyyy-MM-dd') === job.scheduledDate);
    if (startIdx === -1) return 1;
    return Math.min(days, dayView - startIdx);
  };

  const unscheduledJobs = jobs.filter((job) => !job.scheduledDate);
  const filteredUnscheduledJobs = unscheduledJobs.filter((job) => {
    // Client-side text search only when API search is not wired up
    if (!onSearchChange && searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        job.title.toLowerCase().includes(term) ||
        job.description.toLowerCase().includes(term) ||
        job.location?.toLowerCase().includes(term) ||
        job.assignee?.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }

    return true;
  });

  // Flag "new" jobs — those that landed in the planning list within the recent
  // window (the backend bumps updated_time on accept, WRK-5718). New jobs keep
  // the orange highlight and float to the top; the rest order A-Z by title so
  // the list isn't in the API's random order.
  const now = new Date();
  const sortedUnscheduledJobs = sortJobsByTitle(
    filteredUnscheduledJobs.map((job) => ({
      ...job,
      isNew: isWithinRecentWindow(job.updatedTime, now),
    })),
  );

  const scheduledJobsCount = jobs.filter((j) => j.scheduledDate).length;
  const totalHours = Math.round(
    jobs
      .filter((j) => j.scheduledDate)
      .reduce((sum, j) => sum + (j.duration ?? j.estimatedHours * 60), 0) / 60,
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PageHeader
        title="Schedule"
        subtitle={`${scheduledJobsCount} jobs scheduled • ${unscheduledJobs.length} unscheduled • ${totalHours}h total`}
        icon={<CalendarIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
      />

      {/* Auto-load status (WRK-5235) — shown above the board while Lite loads the
          remaining pages of scheduled jobs in the background, so days aren't
          falsely empty. Disappears once every page is loaded. */}
      {scheduledPagination && (
        <div className="px-1 flex-shrink-0">
          <SchedulerPaginationNotice
            isLoadingAll={scheduledPagination.isLoadingAll}
            loaded={scheduledPagination.loaded}
            total={scheduledPagination.total}
          />
        </div>
      )}

      {/* Resource utilization summary + legend — visible in all view modes */}
      <div className="flex items-center justify-between gap-3 mb-3 px-1 flex-shrink-0 flex-wrap">
        <ScheduleUtilizationSummary
          averageUtilization={capacityOverall.averageUtilization}
          teamsOverCapacity={capacityOverall.teamsOverCapacity}
          totalTeams={capacityOverall.totalTeams}
          isLoading={capacityLoading}
        />
        <ScheduleUtilizationLegend />
      </div>

      {/* Controls Row */}
      <div className="flex items-center justify-between gap-3 mb-4 px-1 flex-shrink-0 flex-wrap">
        {/* Left side - Search, then filter dropdowns */}
        <div className="flex items-center gap-3 flex-wrap">
          <ScheduleSearchInput
            initialValue={search ?? searchTerm}
            onDebouncedChange={handleSearchDebouncedChange}
          />

          <Select value={filters.category} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[280px] bg-background border-border text-foreground">
              <SelectValue placeholder="All Job Types" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border max-h-[300px]">
              <SelectItem value="all">All Job Types</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <MultiSelectAutocomplete
            options={teams.map((t) => ({ value: t.id, label: t.name }))}
            selected={selectedTeams}
            onChange={setSelectedTeams}
            placeholder="All Teams"
            className="w-[240px]"
          />

          {/* Area filter (SC-6251) — reintroduced from the legacy Schedule page.
              Carries its own test id so it stays distinguishable from the Teams
              multi-select beside it once a selection hides the placeholder. */}
          <MultiSelectAutocomplete
            options={areas.map((a) => ({ value: String(a.id), label: a.name }))}
            selected={selectedAreas}
            onChange={setSelectedAreas}
            placeholder="All Areas"
            className="w-[240px]"
            triggerTestId="area-multiselect-trigger"
          />

          <CheckboxMultiSelect
            options={priorities}
            selected={selectedPriorities}
            onChange={setSelectedPriorities}
            allLabel="All Priorities"
            className="h-10 w-[160px]"
          />
        </div>

        {/* Right side - Date Navigation, Day/Zoom View and Filters */}
        <div className="flex items-center gap-3">
          <CalendarToolbar
            rangeStart={viewStart}
            daysToShow={visibleDaysToShow}
            onStep={(direction) => {
              if (viewMode === 'gantt') {
                setCurrentDate(
                  ganttStepDate(ganttZoomLevel, currentDate, direction, ganttRangeOverride),
                );
              } else if (viewMode === 'map') {
                setCurrentDate(mapStepDate(currentDate, direction, mapDays));
              } else {
                setCurrentDate(gridStepDate(currentDate, direction, dayView));
              }
            }}
            onToday={() => {
              setCurrentDate(new Date());
              // Per WRK-5567: Today in Gantt should also drop back to Day zoom
              // so a single column is shown. Other views just re-centre.
              if (viewMode === 'gantt') changeGanttZoom('day');
            }}
            viewControls={
              viewMode === 'gantt' ? (
                <GanttViewControls
                  zoomLevel={ganttZoomLevel}
                  onZoomChange={changeGanttZoom}
                  range={ganttRangeOverride}
                  onRangeChange={setGanttRangeOverride}
                />
              ) : viewMode === 'grid' ? (
                <GridViewControls dayView={dayView} onDayViewChange={setDayView} />
              ) : viewMode === 'map' && clientConfig.features.mapViewRangeSelector ? (
                <MapViewControls mapDays={mapDays} onMapDaysChange={setDayView} />
              ) : null
            }
          />

          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('grid')}
              className={`rounded-none h-8 px-3 ${viewMode === 'grid' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
              title="Calendar Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('gantt')}
              className={`rounded-none h-8 px-3 ${viewMode === 'gantt' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
              title="Gantt Timeline View"
            >
              <GanttChart className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('map')}
              className={`rounded-none h-8 px-3 ${viewMode === 'map' ? 'bg-[#E84A2B] text-white hover:bg-[#d43d20] hover:text-white' : ''}`}
              title="Map View"
            >
              <Globe className="w-4 h-4" />
            </Button>
          </div>

          {/* Export buttons - only show in Gantt view */}
          {viewMode === 'gantt' && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPNG}
                disabled={isExporting}
                className="bg-background border-border hover:bg-accent"
                title="Export as PNG image"
              >
                {exportingFormat === 'png' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    PNG
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                disabled={isExporting}
                className="bg-background border-border hover:bg-accent"
                title="Export as PDF document"
              >
                {exportingFormat === 'pdf' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Preparing…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* AI Insights Section */}
      {/* <div className="flex-shrink-0">
        <CalendarAIInsights jobs={jobs} colorTheme={colorTheme} />
      </div> */}

      {/* Main Content */}
      {viewMode === 'map' ? (
        <PlannerMapView
          jobs={[...filteredUnscheduledJobs, ...jobs.filter((j) => j.scheduledDate)].filter((j) => {
            if (!matchesSearchTerm(j)) return false;
            if (selectedTeams.length > 0 && j.teamId && !selectedTeams.includes(j.teamId))
              return false;
            return true;
          })}
          onJobClick={onJobClick}
          teams={teams}
          summariesByTeam={summariesByTeam}
          rangeStart={viewStart}
          rangeDays={mapDays}
        />
      ) : viewMode === 'gantt' ? (
        <div className="flex-1 flex overflow-hidden px-1">
          {/* Left Panel - Unscheduled Jobs (Same as grid view) */}
          {!sidebarCollapsed ? (
            <>
              <div
                className="flex flex-col bg-card/50 border border-border rounded-lg overflow-hidden flex-shrink-0"
                style={{ width: sidebarWidth }}
              >
                <div className="p-3 border-b border-border bg-card/90">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-foreground text-sm">
                      Unscheduled ({filteredUnscheduledJobs.length})
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setSidebarCollapsed(true)}
                      title="Collapse panel"
                    >
                      <PanelLeftClose className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <UnscheduledDropZone onDrop={handleUnschedule}>
                  <div className="space-y-2">
                    {sortedUnscheduledJobs.map((job, index) => (
                      <DraggableJobCard
                        key={job.id}
                        job={job}
                        variant={designVariant}
                        index={index}
                        onJobClick={handleJobClick}
                      />
                    ))}
                    {filteredUnscheduledJobs.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        {hasActiveFilters
                          ? 'No unscheduled jobs match your filters'
                          : 'No unscheduled jobs'}
                      </div>
                    )}
                  </div>
                </UnscheduledDropZone>
              </div>
              {/* Resize handle - matching Calendar mode styling */}
              <div
                className="w-2 flex-shrink-0 cursor-col-resize flex items-center justify-center hover:bg-muted/50 transition-colors group"
                onMouseDown={handleResizeStart}
              >
                <div className="w-0.5 h-8 bg-border group-hover:bg-primary/50 rounded-full transition-colors" />
              </div>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="h-full w-10 flex-shrink-0 rounded-r-none"
              onClick={() => setSidebarCollapsed(false)}
              title="Show unscheduled jobs"
            >
              <PanelLeftOpen className="w-4 h-4" />
            </Button>
          )}

          {/* Gantt Chart View - wrapped for export */}
          <div
            ref={ganttContainerRef}
            id="gantt-chart-container"
            className="flex-1 flex flex-col overflow-hidden"
          >
            <GanttView
              jobs={jobs.filter((job) => {
                // Apply same filters as grid view
                if (!job.scheduledDate) return false;

                if (!matchesSearchTerm(job)) return false;

                if (selectedTeams.length > 0 && !selectedTeams.includes(job.teamId ?? ''))
                  return false;

                return true;
              })}
              teams={teams.filter((team) => {
                if (selectedTeams.length > 0 && !selectedTeams.includes(team.id)) return false;
                return true;
              })}
              onUpdateJob={onUpdateJob}
              onJobClick={handleJobClick}
              onDrop={handleDrop}
              zoomLevel={ganttZoomLevel}
              daysToShow={ganttRange.days}
              rangeStart={ganttRange.start}
              viewportWidth={ganttViewportWidth}
              capacitiesByTeam={capacitiesByTeam}
              summariesByTeam={summariesByTeam}
              membersByTeam={membersByTeam}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden px-1">
          {/* Left Panel - Unscheduled Jobs (Resizable + Collapsible) */}
          {!sidebarCollapsed ? (
            <>
              <div
                className="flex flex-col bg-card/50 border border-border rounded-lg overflow-hidden flex-shrink-0"
                style={{ width: sidebarWidth }}
              >
                <div className="p-3 border-b border-border bg-card/90">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-foreground text-sm">
                      Unscheduled ({filteredUnscheduledJobs.length})
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setSidebarCollapsed(true)}
                      title="Collapse panel"
                    >
                      <PanelLeftClose className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <UnscheduledDropZone onDrop={handleUnschedule}>
                  <div className="space-y-2">
                    {sortedUnscheduledJobs.map((job, index) => (
                      <DraggableJobCard
                        key={job.id}
                        job={job}
                        variant={designVariant}
                        index={index}
                        onJobClick={handleJobClick}
                      />
                    ))}
                    {filteredUnscheduledJobs.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-xs">
                          {hasActiveFilters
                            ? 'No unscheduled jobs match your filters'
                            : 'All jobs scheduled!'}
                        </p>
                      </div>
                    )}
                  </div>
                </UnscheduledDropZone>
              </div>

              {/* Resize Handle */}
              <div
                className="w-2 flex-shrink-0 cursor-col-resize flex items-center justify-center hover:bg-muted/50 transition-colors group"
                onMouseDown={handleResizeStart}
              >
                <div className="w-0.5 h-8 bg-border group-hover:bg-primary/50 rounded-full transition-colors" />
              </div>
            </>
          ) : (
            <div className="flex-shrink-0 mr-2">
              <Button
                variant="outline"
                size="sm"
                className="h-full px-1.5 border-border rounded-lg"
                onClick={() => setSidebarCollapsed(false)}
                title="Show unscheduled jobs"
              >
                <div className="flex flex-col items-center gap-1">
                  <PanelLeftOpen className="w-4 h-4" />
                  <span className="text-xs [writing-mode:vertical-lr] rotate-180">
                    Unscheduled ({filteredUnscheduledJobs.length})
                  </span>
                </div>
              </Button>
            </div>
          )}

          {/* Right Panel - Calendar Grid */}
          <div className="flex-1 flex flex-col bg-card/50 border border-border rounded-lg overflow-hidden">
            <div className="flex-1 min-h-0 overflow-auto">
              <div className="min-w-[800px]">
                {/* Header Row - Days */}
                <div
                  className="grid border-b border-border sticky top-0 z-20 bg-card"
                  style={{ gridTemplateColumns: `150px repeat(${dayView}, 1fr)` }}
                >
                  <div className="p-3 border-r border-border bg-card sticky left-0 z-10">
                    <span className="text-sm font-medium text-muted-foreground">Team</span>
                  </div>
                  {weekDays.map((day) => {
                    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    return (
                      <div
                        key={day.toISOString()}
                        className="p-3 border-r border-border last:border-r-0 bg-card"
                      >
                        <div className="text-center">
                          <div className="text-xs text-muted-foreground mb-0.5">
                            {format(day, 'EEE')}
                          </div>
                          <div
                            className={`text-sm font-medium ${
                              isToday ? 'text-blue-600 dark:text-blue-400' : 'text-foreground'
                            }`}
                          >
                            {format(day, 'd MMM')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <CalendarGridTeamRows
                  teams={teams}
                  selectedTeams={selectedTeams}
                  teamFilter="all"
                  weekDays={weekDays}
                  summariesByTeam={summariesByTeam}
                  renderCell={(team, day) => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const cellJobs = getJobsForCell(team.id, dateStr);
                    const spans = new Map<string, number>();
                    for (const j of cellJobs) {
                      spans.set(j.id, getJobSpanDays(j));
                    }
                    // SC-6668: block booking into a team/day that has reached its
                    // Daily Job Capacity (per day; other days are unaffected).
                    const bookedCount = bookedJobCountsByTeamDate.get(`${team.id}|${dateStr}`) ?? 0;
                    const atCapacity = isTeamDayAtCapacity(bookedCount, team.maxCapacity);
                    return (
                      <CalendarCell
                        key={`${team.id}-${dateStr}`}
                        teamId={team.id}
                        date={dateStr}
                        jobs={cellJobs}
                        jobSpans={spans}
                        onDrop={handleDrop}
                        variant={designVariant}
                        onJobClick={handleJobClick}
                        atCapacity={atCapacity}
                      />
                    );
                  }}
                />
              </div>
            </div>

            {/* Scheduled-jobs load status (WRK-5235) — pages auto-load in the
                background; this footer just reports progress, no user action. */}
            {scheduledPagination && scheduledPagination.total > 0 && (
              <div className="flex items-center justify-center gap-2 border-t border-border py-3 flex-shrink-0">
                {scheduledPagination.isLoadingAll && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                )}
                <span className="text-xs text-muted-foreground">
                  {scheduledPagination.isLoadingAll
                    ? `Loading all scheduled jobs (${scheduledPagination.loaded} of ${scheduledPagination.total})…`
                    : `Showing all ${scheduledPagination.total} scheduled jobs`}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Auto Schedule Modal */}
      <AIAutoScheduleModal
        open={showAIScheduleModal}
        onOpenChange={setShowAIScheduleModal}
        jobs={jobs}
        onUpdateJob={onUpdateJob}
      />

      {/* Schedule Time Picker Modal */}
      <Dialog
        open={!!schedulePrompt}
        onOpenChange={(open) => {
          if (!open && !isSubmittingSchedule) setSchedulePrompt(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Schedule Job</DialogTitle>
            <DialogDescription>
              Set the schedule for{' '}
              <span className="font-medium text-foreground">{schedulePrompt?.job.title}</span>
            </DialogDescription>
          </DialogHeader>

          {schedulePrompt && (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-muted/50 border border-border p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start Date</span>
                  <span className="font-medium text-foreground">
                    {new Date(schedulePrompt.date).toLocaleDateString('en-GB', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated Duration</span>
                  <span className="font-medium text-foreground">
                    {formatMinutesCompact(schedulePrompt.job.duration)}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="schedule-start-time"
                  className="text-sm font-medium text-foreground"
                >
                  Start Time
                </label>
                <Input
                  id="schedule-start-time"
                  type="time"
                  value={scheduleStartTime}
                  onChange={(e) => {
                    setScheduleStartTime(e.target.value);
                    const durationMinutes =
                      schedulePrompt.job.duration ?? (schedulePrompt.job.estimatedHours ?? 1) * 60;
                    const newEnd = new Date(
                      zonedWallTimeToInstant(
                        schedulePrompt.date,
                        e.target.value,
                        schedulingTimeZone,
                      ).getTime() +
                        durationMinutes * 60 * 1000,
                    );
                    setScheduleEndDate(formatZonedDate(newEnd, schedulingTimeZone));
                    setScheduleEndTime(formatZonedTime(newEnd, schedulingTimeZone));
                  }}
                  className="w-full"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label
                    htmlFor="schedule-end-date"
                    className="text-sm font-medium text-foreground"
                  >
                    End Date
                  </label>
                  <Input
                    id="schedule-end-date"
                    type="date"
                    value={scheduleEndDate}
                    min={schedulePrompt.date}
                    onChange={(e) => setScheduleEndDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="schedule-end-time"
                    className="text-sm font-medium text-foreground"
                  >
                    End Time
                  </label>
                  <Input
                    id="schedule-end-time"
                    type="time"
                    value={scheduleEndTime}
                    onChange={(e) => setScheduleEndTime(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              {(() => {
                const durationMinutes =
                  schedulePrompt.job.duration ?? (schedulePrompt.job.estimatedHours ?? 1) * 60;
                const startDt = zonedWallTimeToInstant(
                  schedulePrompt.date,
                  scheduleStartTime,
                  schedulingTimeZone,
                );
                const endDt = zonedWallTimeToInstant(
                  scheduleEndDate,
                  scheduleEndTime,
                  schedulingTimeZone,
                );
                const minEndDt = new Date(startDt.getTime() + durationMinutes * 60 * 1000);
                const {
                  diffMinutes,
                  isBeforeStart,
                  isBelowDuration,
                  isAboveDuration,
                  isInPast,
                  isValid,
                } = computeDurationStatus({
                  durationMinutes,
                  start: startDt,
                  end: endDt,
                  durationChallengeEnabled: clientConfig.features.durationChallenge,
                  now: new Date(),
                });

                let message = '';
                let colorClass =
                  'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800';
                let textClass = 'text-blue-700 dark:text-blue-300';
                let valueClass = 'text-blue-900 dark:text-blue-100';

                if (isInPast) {
                  message = 'Invalid — start time is in the past';
                  colorClass = 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800';
                  textClass = 'text-red-700 dark:text-red-300';
                } else if (isBeforeStart) {
                  message = 'Invalid — end must be after start';
                  colorClass = 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800';
                  textClass = 'text-red-700 dark:text-red-300';
                } else if (isBelowDuration) {
                  message = `End is before the estimated duration of ${formatMinutesCompact(durationMinutes)} (minimum: ${format(minEndDt, 'HH:mm')}${format(minEndDt, 'yyyy-MM-dd') !== schedulePrompt.date ? ` on ${format(minEndDt, 'd MMM')}` : ''})`;
                  colorClass =
                    'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800';
                  textClass = 'text-amber-700 dark:text-amber-300';
                } else if (isAboveDuration) {
                  message = `Selected duration (${formatMinutesCompact(diffMinutes)}) exceeds the estimated ${formatMinutesCompact(durationMinutes)} — use "Duration Challenge" to ask for confirmation.`;
                  colorClass =
                    'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800';
                  textClass = 'text-orange-700 dark:text-orange-300';
                }

                return (
                  <div className={`rounded-md p-3 text-sm border ${colorClass}`}>
                    {!isValid ? (
                      <p className={textClass}>{message}</p>
                    ) : (
                      <div className="flex justify-between">
                        <span className={textClass}>Total Duration</span>
                        <span className={`font-medium ${valueClass}`}>
                          {formatMinutesCompact(diffMinutes)}
                          {(() => {
                            const spannedDays = computeSpannedDays(
                              schedulePrompt.date,
                              scheduleEndDate,
                              scheduleEndTime,
                            );
                            if (spannedDays <= 1) return null;
                            return (
                              <span className="ml-1 text-xs text-blue-600 dark:text-blue-400">
                                (spans {spannedDays} days)
                              </span>
                            );
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSchedulePrompt(null)}
              disabled={isSubmittingSchedule}
            >
              Cancel
            </Button>
            {(() => {
              if (!schedulePrompt) return null;
              const durationMinutes =
                schedulePrompt.job.duration ?? (schedulePrompt.job.estimatedHours ?? 1) * 60;
              const startDt = zonedWallTimeToInstant(
                schedulePrompt.date,
                scheduleStartTime,
                schedulingTimeZone,
              );
              const endDt = zonedWallTimeToInstant(
                scheduleEndDate,
                scheduleEndTime,
                schedulingTimeZone,
              );
              const { diffMinutes, isBeforeStart, isAboveDuration, isInPast } =
                computeDurationStatus({
                  durationMinutes,
                  start: startDt,
                  end: endDt,
                  durationChallengeEnabled: clientConfig.features.durationChallenge,
                  now: new Date(),
                });

              if (isAboveDuration && !isInPast) {
                return (
                  <Button
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => {
                      durationChallengeMutation.mutate({
                        jobId: parseInt(schedulePrompt.job.id),
                        diffHours: diffMinutes / 60,
                        windowStartDate: undefined,
                        windowEndDate: undefined,
                      });
                      setSchedulePrompt(null);
                    }}
                    disabled={isSubmittingSchedule}
                  >
                    Duration Challenge
                  </Button>
                );
              }

              return (
                <Button
                  className="text-white"
                  style={{ backgroundColor: '#E84A2B' }}
                  onClick={confirmSchedule}
                  disabled={isBeforeStart || isInPast || isSubmittingSchedule}
                >
                  {isSubmittingSchedule ? 'Scheduling…' : 'Schedule'}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unschedule Confirmation Modal */}
      <Dialog
        open={!!unscheduleConfirm}
        onOpenChange={(open) => {
          if (!open) setUnscheduleConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Unschedule Job</DialogTitle>
            <DialogDescription>
              Are you sure you want to unschedule{' '}
              <span className="font-medium text-foreground">{unscheduleConfirm?.title}</span>? It
              will be moved back to the unscheduled jobs list.
            </DialogDescription>
          </DialogHeader>
          {unscheduleConfirm?.scheduledDate && (
            <div className="rounded-md bg-muted/50 border border-border p-3 text-sm text-muted-foreground">
              Currently scheduled for{' '}
              <span className="font-medium text-foreground">
                {new Date(unscheduleConfirm.scheduledDate).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnscheduleConfirm(null)}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={confirmUnschedule}
            >
              Unschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Details Modal */}
      <JobDetailsModal
        open={showJobDetailsModal}
        onOpenChange={setShowJobDetailsModal}
        job={jobs.find((j) => j.id === selectedJobId) || null}
        onViewFullDetails={(jobId) => {
          // Close the modal and trigger the full details view
          setShowJobDetailsModal(false);
          if (onJobClick) {
            onJobClick(jobId);
          }
        }}
        onViewFieldWorker={(jobId) => {
          // Navigate to field worker mobile view
          setShowJobDetailsModal(false);
          if (onNavigateToFieldWorker) {
            onNavigateToFieldWorker(jobId);
          } else {
            window.location.href = `/field-worker?jobId=${jobId}`;
          }
        }}
      />
    </div>
  );
}
